/*
 * Collection-level personalized preview.
 * Same pp.totemteam.com endpoints as totem-pp.js (product page), but one animal/quality
 * selection updates the preview image of every [data-totem-sku] card in the collection grid.
 */
if (!customElements.get('totem-collection-preview')) {

  const PP_DOMAIN = 'https://pp.totemteam.com';
  const POLL_DELAY = 1000;
  const MAX_POLLS = 30;

  const buildAnimalUrl = (sku, lang) => `${PP_DOMAIN}/public/${lang}/cards/animals/${sku}.json`;
  const buildQualityUrl = (sku, lang, animalKey) => `${PP_DOMAIN}/public/${lang}/cards/qualities/${sku}/${animalKey}.json`;
  const buildImageUrl = (sku, lang, animalKey, qualityKey) => `${PP_DOMAIN}/public/${lang}/product_mockups/${sku}/${animalKey}/${qualityKey}.json`;

  const getParameterByName = (name) => new URLSearchParams(window.location.search).get(name);

  class TotemCollectionPreview extends HTMLElement {

    connectedCallback() {
      this.lang = this.dataset.lang || 'en';
      this.animalSelect = this.querySelector('[data-js-totem-animals]');
      this.qualitySelect = this.querySelector('[data-js-totem-qualities]');
      this.generation = 0;

      this.refreshCards();
      if (this.cards.length === 0) {
        this.hidden = true;
        return;
      }
      // Animal/quality cards are shared across products; use the first personalized product to fetch the lists
      this.listSku = this.cards[0].dataset.totemSku;

      this.animalSelect.addEventListener('change', this.onChangeAnimal.bind(this));
      this.qualitySelect.addEventListener('change', this.onChangeQuality.bind(this));

      // Filtering/sorting (component-facets.js) re-renders the grid: re-apply the selection on the new cards
      const grid = document.getElementById('CollectionProductGrid');
      if (grid) {
        new MutationObserver(() => {
          this.refreshCards();
          this.updatePreviews();
        }).observe(grid, { childList: true });
      }

      this.initFloating();
      this.init();
    }

    // Pin the dropdowns at the top of the screen while the product grid is in view and their original spot is scrolled past
    initFloating() {
      this.bar = this.querySelector('[data-js-totem-bar]');
      if (!this.bar) return;

      let ticking = false;
      const onScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          this.updateFloating();
        });
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll);
      this.updateFloating();
    }

    updateFloating() {
      // Stay below a sticky header if the theme has one enabled
      const header = document.getElementById('site-header');
      let top = 0;
      if (header && header.hasAttribute('data-sticky-header')) {
        top = Math.max(0, header.getBoundingClientRect().bottom);
      }

      const grid = document.getElementById('CollectionProductGrid');
      const spot = this.getBoundingClientRect();
      const barHeight = this.bar.offsetHeight;
      const shouldFloat = spot.bottom < top && (!grid || grid.getBoundingClientRect().bottom > top + barHeight);

      this.style.setProperty('--totem-floating-top', `${top}px`);
      if (shouldFloat === this.classList.contains('is-floating')) return;

      if (shouldFloat) {
        // Keep the original spot's height so the page doesn't jump when the bar detaches
        this.style.minHeight = `${this.offsetHeight}px`;
        this.classList.add('is-floating');
      } else {
        this.classList.remove('is-floating');
        this.style.minHeight = '';
      }
    }

    refreshCards() {
      this.cards = Array.from(document.querySelectorAll('[data-js-product-item][data-totem-sku]'));

      // Open the cart drawer after a card add-to-cart, like the product page (section-main-product.js)
      this.cards.forEach((card) => {
        const form = card.querySelector('[data-js-totem-card-form]');
        if (!form || form._totemBound) return;
        form._totemBound = true;
        form.addEventListener('add-to-cart', () => {
          const cartSidebar = document.getElementById('site-cart-sidebar');
          if (cartSidebar) cartSidebar.show();
          const recommendations = document.getElementById('cart-recommendations-sidebar');
          if (recommendations) recommendations.generateRecommendations();
        });
      });
    }

    // Fill the card's cart line-item properties (same fields as the product page form) and toggle its button
    updateCardForms() {
      const animalOption = this.animalSelect.selectedOptions[0];
      const qualityOption = this.qualitySelect.selectedOptions[0];
      const ready = Boolean(this.animalSelect.value && this.qualitySelect.value);
      const values = {
        'animal': ready ? this.animalSelect.value : '',
        'quality': ready ? this.qualitySelect.value : '',
        'animal-text': ready ? animalOption.text : '',
        'quality-text': ready ? qualityOption.text : ''
      };

      this.cards.forEach((card) => {
        Object.entries(values).forEach(([field, value]) => {
          const input = card.querySelector(`[data-totem-field="${field}"]`);
          if (input) input.value = value;
        });

        const button = card.querySelector('[data-js-totem-add-to-cart]');
        if (!button) return;
        const text = button.querySelector('[data-js-totem-add-to-cart-text]');
        const available = button.dataset.available === 'true';
        const enabled = ready && available;
        button.disabled = !enabled;
        button.classList.toggle('disabled', !enabled);
        text.textContent = !available ? text.dataset.labelSoldOut : (ready ? text.dataset.labelAdd : text.dataset.labelChoose);
      });
    }

    async init() {
      // ?aq=animal_quality, same format as the product page. Animal keys may contain "_" (e.g. black_bird),
      // so match the longest known animal key prefix instead of splitting.
      const aq = getParameterByName('aq');

      await this.loadOptions(this.animalSelect, buildAnimalUrl(this.listSku, this.lang), null);

      let animal = null;
      let quality = null;
      if (aq) {
        Array.from(this.animalSelect.options).forEach((option) => {
          if (option.value && aq.startsWith(`${option.value}_`) && (!animal || option.value.length > animal.length)) {
            animal = option.value;
          }
        });
        if (animal) {
          this.animalSelect.value = animal;
          quality = aq.slice(animal.length + 1);
        }
      }

      await this.loadOptions(this.qualitySelect, buildQualityUrl(this.listSku, this.lang, animal || 'ant'), quality);

      this.updatePreviews();
    }

    async onChangeAnimal() {
      const animal = this.animalSelect.value || 'ant';
      // Keep whatever quality is selected when the new list arrives (it may change while the list is loading)
      const loaded = await this.loadOptions(this.qualitySelect, buildQualityUrl(this.listSku, this.lang, animal));
      if (loaded) this.updatePreviews();
    }

    onChangeQuality() {
      this.updatePreviews();
    }

    // Returns false when a newer request for the same select superseded this one
    async loadOptions(select, url, selectedKey) {
      const token = (select._totemRequest = (select._totemRequest || 0) + 1);
      try {
        const response = await fetch(url);
        const json = await response.json();
        if (token !== select._totemRequest) return false;
        if (selectedKey == null) selectedKey = select.value;
        const placeholder = select.options[0];

        select.replaceChildren(placeholder);
        (json.data || []).forEach((item) => {
          const card = item.attributes;
          const option = new Option(`${card.title} - ${card.content}`, card.key);
          option.selected = card.key === selectedKey;
          select.appendChild(option);
        });
        return true;
      } catch (error) {
        console.error('[totem-collection-preview] failed to load', url, error);
        return false;
      }
    }

    updatePreviews() {
      const animal = this.animalSelect.value;
      const quality = this.qualitySelect.value;
      const generation = ++this.generation;
      this.updateCardForms();

      if (!animal || !quality) {
        this.cards.forEach((card) => this.resetCard(card));
        this.updateUrls(null);
        return;
      }

      const aq = `${animal}_${quality}`;
      this.updateUrls(aq);
      this.cards.forEach((card) => this.loadCardPreview(card, animal, quality, generation, 0));
    }

    async loadCardPreview(card, animal, quality, generation, attempt) {
      const url = buildImageUrl(card.dataset.totemSku, this.lang, animal, quality);
      card.classList.add('totem-preview--loading');

      try {
        const response = await fetch(url);
        const json = await response.json();
        if (generation !== this.generation) return;

        const image = json.data ? json.data.attributes : null;
        if (!image) {
          // No mockup for this product: keep its regular image
          this.resetCard(card);
          return;
        }

        if (image.preview_ready === false) {
          if (attempt < MAX_POLLS) {
            setTimeout(() => {
              if (generation === this.generation) this.loadCardPreview(card, animal, quality, generation, attempt + 1);
            }, POLL_DELAY);
          } else {
            card.classList.remove('totem-preview--loading');
          }
          return;
        }

        this.setCardImage(card, image.url);
      } catch (error) {
        if (generation === this.generation) card.classList.remove('totem-preview--loading');
        console.error('[totem-collection-preview] failed to load', url, error);
      }
    }

    setCardImage(card, src) {
      const container = card.querySelector('[data-js-product-item-image]');
      let img = container.querySelector('.totem-preview__img');
      if (!img) {
        img = document.createElement('img');
        img.className = 'totem-preview__img';
        img.alt = '';
        container.appendChild(img);
      }
      img.onload = () => card.classList.remove('totem-preview--loading');
      img.src = src;
      this.setCardZoom(container, src);
      card.classList.add('totem-preview--active');
    }

    // Loupe over the preview, using the product page zoom (component-product-image-zoom.js).
    // The image is wrapped in the product link, so the click must not navigate.
    setCardZoom(container, src) {
      let zoom = container.querySelector('.totem-preview__zoom');
      if (!zoom) {
        zoom = document.createElement('product-image-zoom');
        zoom.className = 'totem-preview__zoom';
        zoom.setAttribute('role', 'button');
        zoom.setAttribute('aria-label', this.lang === 'fr' ? 'Agrandir l’image' : 'Enlarge image');
        const icon = document.createElement('span');
        icon.className = 'totem-preview__zoom-icon';
        icon.innerHTML = (window.KROWN && KROWN.settings.symbols.zoom_in) || '';
        zoom.appendChild(icon);
        zoom.addEventListener('click', (e) => e.preventDefault());
        container.appendChild(zoom);
      }
      zoom.setAttribute('data-image', src);
    }

    resetCard(card) {
      card.querySelectorAll('.totem-preview__img, .totem-preview__zoom').forEach((el) => el.remove());
      card.classList.remove('totem-preview--active', 'totem-preview--loading');
    }

    // Keep ?aq= on the collection URL and on the product links, so the product page opens with the same selection
    updateUrls(aq) {
      const pageUrl = new URL(window.location.href);
      if (aq) pageUrl.searchParams.set('aq', aq); else pageUrl.searchParams.delete('aq');
      history.replaceState(history.state, '', pageUrl);

      this.cards.forEach((card) => {
        card.querySelectorAll('a[href]').forEach((link) => {
          const linkUrl = new URL(link.href, window.location.origin);
          if (aq) linkUrl.searchParams.set('aq', aq); else linkUrl.searchParams.delete('aq');
          link.href = linkUrl.pathname + linkUrl.search + linkUrl.hash;
        });
      });
    }
  }

  customElements.define('totem-collection-preview', TotemCollectionPreview);
}
